<template>
  <h3 class="lead-view-list-title">
    <BIcon
      rounded
    >
      list
    </BIcon>
    <span>{{ $t('home.leadViewList.title') }}</span>
  </h3>
  <div
    v-loading="loading"
    class="lead-view-list"
  >
    <div class="scroll-container">
      <div class="list-container">
        <template v-if="!loading">
          <template v-if="leadViews.length === 0">
            <BEmptyBox
              class="empty-lead-view-list"
              display-only
            >
              {{ $t('home.leadViewList.empty') }}
            </BEmptyBox>
          </template>
          <template v-else>
            <LeadViewCard
              v-for="leadView in leadViews"
              :key="leadView.id"
              :lead-view="leadView"
              @click="() => handleSelectLeadView(leadView)"
              @edit="() => handleLeadViewEdit(leadView)"
              @after-delete="handleAfterDelete"
            />
            <BEmptyBox
              v-for="i in 3 - leadViews.length"
              :key="i"
              class="empty-lead-view-card"
              display-only
            />
          </template>
        </template>
      </div>
    </div>
    <LeadViewSelect
      @select-lead-view="handleSelectLeadView"
    >
      <template #activator>
        <BBtn
          outline
          type="primary"
          class="lead-view-list-button"
        >
          <BElementIcon
            name="search"
            size="large"
          />
          {{ $t('home.leadViewList.search') }}
        </BBtn>
      </template>
    </LeadViewSelect>
    <LeadViewUpdateModal
      :modal-name="LEAD_VIEW_UPDATE_MODAL"
      :lead-view="selectedLeadView"
      @after-update="handleAfterUpdate"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeMount, ref } from 'vue';
import type { LeadView } from '@/api/openapi';
import { LeadViewApiService } from '@/api/user/resources/lead_view';
import { LeadViewLogApiService } from '@/api/user/resources/lead_view_log';
import { useModal } from '@/composable/modal';
import { useRouterLink } from '@/composable/router-link';
import { useFilterParams } from '@/composable/user/leadList/lead-filter';
import { useCurrentUser } from '@/composable/user/user/users';
import { useWait } from '@/composable/vue-wait';
import BEmptyBox from '@/plugins/biscuet-materials/components/molecules/BEmptyBox.vue';
import LeadViewCard from '../general/LeadViewCard.vue';
import LeadViewSelect from '../general/leadViewSelect/LeadViewSelect.vue';
import LeadViewUpdateModal from '../general/modal/leadView/LeadViewUpdateModal.vue';

const NUMBER_OF_LEAD_VIEW = 3;
const LEAD_VIEW_UPDATE_MODAL = 'cockPitLeadViewUpdateModal';
const api = new LeadViewApiService();
const logApi = new LeadViewLogApiService();
const { doActionWithWait, wait } = useWait();
const { openModal } = useModal(() => LEAD_VIEW_UPDATE_MODAL);
const { currentUser, fetchCurrentUser } = useCurrentUser();
const { linkToOrReload } = useRouterLink();
const { clearLeadListFilterParams } = useFilterParams();

const leadViews = ref<LeadView[]>([]);
const selectedLeadView = ref<LeadView>(undefined);
const loading = computed(() => {
  return wait.is('fetchRecentlyMostUsedLeadViews');
});

onBeforeMount(async () => {
  await fetchCurrentUser();
  await fetchRecentlyMostUsedLeadViews();
});

const fetchRecentlyMostUsedLeadViews = async () => {
  await doActionWithWait('fetchRecentlyMostUsedLeadViews', async () => {
    const { data } = await api.getRecentlyMostUsedLeadViews({
      request: {
        limit: NUMBER_OF_LEAD_VIEW,
        userId: currentUser.value.id,
      },
    });
    leadViews.value = data.leadViews;
  });
};

const handleLeadViewEdit = (leadView: LeadView) => {
  selectedLeadView.value = leadView;
  openModal();
};
const handleAfterUpdate = async () => {
  selectedLeadView.value = undefined;
  await fetchRecentlyMostUsedLeadViews();
};
const handleAfterDelete = async () => {
  await fetchRecentlyMostUsedLeadViews();
};
const handleSelectLeadView = async (leadView: LeadView | null) => {
  await clearLeadListFilterParams();
  if (leadView === null) {
    linkToOrReload({ name: 'LeadList' });
    return;
  }
  linkToOrReload({ name: 'LeadList', query: { leadViewId: leadView.id } });
  await logApi.postLeadViewLog({
    request: {
      leadViewLogBody: {
        leadViewId: leadView.id,
        event: 'click',
      },
    },
  });
};
</script>

<style lang="scss" scoped>
$lead-view-card-height: 140px;
$lead-view-card-min-width: 280px;
.lead-view-list-title {
  padding-bottom: $basespace-200;
  .b-icon {
    padding-right: $basespace-100;
  }
}
.lead-view-list {
  @include m-flex-center;

  .scroll-container {
    flex: 1;
    overflow-x: scroll;
  }
  .list-container {
    display: flex;
    flex-wrap: nowrap;

    .empty-lead-view-list {
      @include m-flex-center;
      width: 100%;
      height: $lead-view-card-height;
    }
    .lead-view-card, .empty-lead-view-card {
      height: $lead-view-card-height;
      width: calc(100% / 3 - $basespace-100);
      min-width: $lead-view-card-min-width;
      &:not(:last-child) {
        margin-right: $basespace-100;
      }
    }
    .lead-view-card :deep(.title) {
      @include m-truncate-text-multi-line(4);
    }
  }

  .lead-view-list-button {
    @include m-break-word-pre-wrap;
    height: $lead-view-card-height;
    width: 140px;
    margin-left: $basespace-200;
    line-height: 1.2;
    :deep(.inner) {
      display: flex;
      flex-direction: column;
    }
    .b-icon {
      padding-bottom: $basespace-100;
    }
  }
  .b-modal {
    z-index: 10000;
  }
}
</style>

